/**
 * Firmware upload helpers (ADR 0028 amendment, 2026-09-13).
 *
 * Kept pure so the size display and the error mapping are unit-testable:
 * the operator's first real image hit the gateway's body cap and the form
 * said only "failed" — the 413 comes from nginx with an HTML body, so the
 * API client has no `detail` to surface. The limit is stated up front and
 * the 413 is named.
 */

import { ApiError } from "@pbxmgr/api";

/** The upload gateway's body cap (nginx `client_max_body_size` for /api/firmware/images). */
export const UPLOAD_LIMIT_MB = 200;

/** Human-readable size in MB with one decimal (e.g. "34.2 MB"). */
export function formatMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Whether a selected file exceeds the gateway cap (refuse client-side, with the reason). */
export function exceedsLimit(bytes: number): boolean {
  return bytes > UPLOAD_LIMIT_MB * 1024 * 1024;
}

/**
 * Model and version implied by a Snom image filename, e.g.
 * `snomD815-10.1.226.13-SIP-r.swu` → D815 / 10.1.226.13 (the D8xx `.swu`
 * naming seen on the lab upload, 2026-09-13; older series use `.bin`).
 * Either field is omitted when the name does not carry it.
 */
export function guessFromFilename(name: string): {
  model?: string;
  version?: string;
} {
  const m = /^snom([A-Za-z0-9]+?)-(\d+(?:\.\d+)+)/i.exec(name.trim());
  if (!m) return {};
  return { model: m[1].toUpperCase(), version: m[2] };
}

/** The message to show for a failed upload. */
export function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413) {
      return `Image too large for the upload gateway (limit ${UPLOAD_LIMIT_MB} MB).`;
    }
    if (err.status === 0)
      return "Network error during upload — check the connection and retry.";
    return err.detail ?? err.message;
  }
  return err instanceof Error ? err.message : "upload failed";
}
